/**
 * Tabbed Media Section
 * Handles tab switching, image/video slides, and navigation
 */

class TabbedMedia {
  constructor(section) {
    this.section = section;
    this.currentSlideIndex = 1;
    this.totalSlides = section.querySelectorAll('.banner-with-tabbing-main-image').length;
    this.isTransitioning = false;
    this.tabHeader = section.querySelector('.banner-with-tab-header');
    this.highlight = section.querySelector('.tab-highlight');
    this.leftArrow = section.querySelector('.tab-arrow--left');
    this.rightArrow = section.querySelector('.tab-arrow--right');
    this.imageContainer = section.querySelector('.banner-with-tabbing-main-image-content');

    this.init();
  }

  init() {
    // Set total slides count
    this.section.querySelectorAll('.banner-with-tabbing-main-image-content').forEach((el) => {
      el.setAttribute('data-count', this.totalSlides);
    });

    // Activate first slide
    const firstHeader = this.section.querySelector('.tab-header');
    if (firstHeader) firstHeader.classList.add('active');

    const firstTab = this.section.querySelector('.banner-tab');
    if (firstTab) firstTab.classList.add('active');

    const firstImage = this.section.querySelector('.banner-with-tabbing-main-image');
    if (firstImage) firstImage.classList.add('active');

    // Initialize arrow visibility and highlight
    this.updateArrowVisibility();
    this.updateHighlight();

    // Event listeners
    if (this.tabHeader) {
      this.tabHeader.addEventListener('scroll', () => { this.updateArrowVisibility(); this.updateHighlight(); });
      window.addEventListener('resize', () => {
        setTimeout(() => { this.updateArrowVisibility(); this.updateHighlight(); }, 100);
      });
      setTimeout(() => { this.updateArrowVisibility(); this.updateHighlight(); }, 100);
    }

    // Tab header clicks
    this.section.querySelectorAll('.tab-header').forEach((el) => {
      el.addEventListener('click', () => {
        if (this.isTransitioning) return;
        const newIndex = parseInt(el.getAttribute('data-id'), 10);
        if (newIndex !== this.currentSlideIndex) {
          this.currentSlideIndex = newIndex;
          this.activateSlideWithFade(this.currentSlideIndex);
          this.updateHighlight();
        }
      });
    });

    // Navigation buttons
    this.section.querySelectorAll('.nav-button').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.isTransitioning) return;
        const direction = parseInt(btn.getAttribute('data-direction'), 10);
        this.navigateSlide(direction);
      });
    });

    // Tab arrow clicks
    if (this.leftArrow) {
      this.leftArrow.addEventListener('click', () => this.scrollTabs(-200));
    }
    if (this.rightArrow) {
      this.rightArrow.addEventListener('click', () => this.scrollTabs(200));
    }

    // Video play buttons
    const playButtons = this.section.querySelectorAll('.video-play-btn');
    playButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-target');
        const video = this.section.querySelector('#' + id);
        const wrap = btn.closest('.video-wrapper');
        if (video) {
          video.play();
        }
        if (wrap) {
          wrap.classList.add('is-playing');
        }
        btn.classList.add('is-hidden');
        btn.setAttribute('aria-hidden', 'true');
      });
    });

    // Video play / pause events + click-to-toggle
    this.section.querySelectorAll('.custom-video').forEach((v) => {
      v.addEventListener('play', () => {
        const w = v.closest('.video-wrapper');
        if (w) {
          w.classList.add('is-playing');
          const b = w.querySelector('.video-play-btn');
          if (b) {
            b.classList.add('is-hidden');
            b.setAttribute('aria-hidden', 'true');
          }
        }
      });

      v.addEventListener('pause', () => {
        const w = v.closest('.video-wrapper');
        if (w) {
          w.classList.remove('is-playing');
          const b = w.querySelector('.video-play-btn');
          if (b) {
            b.classList.remove('is-hidden');
            b.removeAttribute('aria-hidden');
          }
        }
      });

      v.addEventListener('click', () => {
        if (v.paused || v.ended) {
          v.play();
        } else {
          v.pause();
        }
      });
    });

    // Keyboard navigation
    this.section.setAttribute('tabindex', '0');
    this.section.addEventListener('keydown', (e) => {
      if (this.isTransitioning) return;
      if (e.key === 'ArrowLeft') {
        this.navigateSlide(-1);
      } else if (e.key === 'ArrowRight') {
        this.navigateSlide(1);
      }
    });
    this.initTouchEvents();
  }

  updateArrowVisibility() {
    if (!this.tabHeader || !this.leftArrow || !this.rightArrow) return;

    if (window.innerWidth <= 767) {
      const scrollWidth = this.tabHeader.scrollWidth;
      const clientWidth = this.tabHeader.clientWidth;
      const scrollLeft = this.tabHeader.scrollLeft;

      if (scrollWidth > clientWidth) {
        if (scrollLeft <= 0) {
          this.leftArrow.classList.add('hidden');
        } else {
          this.leftArrow.classList.remove('hidden');
        }

        if (scrollLeft >= scrollWidth - clientWidth - 1) {
          this.rightArrow.classList.add('hidden');
        } else {
          this.rightArrow.classList.remove('hidden');
        }
      } else {
        this.leftArrow.classList.add('hidden');
        this.rightArrow.classList.add('hidden');
      }
    } else {
      this.leftArrow.classList.add('hidden');
      this.rightArrow.classList.add('hidden');
    }
  }

  scrollTabs(amount) {
    if (this.tabHeader) {
      this.tabHeader.scrollBy({
        left: amount,
        behavior: 'smooth',
      });
    }
  }

  navigateSlide(direction) {
    let newIndex = this.currentSlideIndex + direction;
    if (newIndex > this.totalSlides) {
      this.currentSlideIndex = 1;
    } else if (newIndex < 1) {
      this.currentSlideIndex = this.totalSlides;
    } else {
      this.currentSlideIndex = newIndex;
    }
    this.activateSlideWithFade(this.currentSlideIndex);
  }

  activateSlideWithFade(slideIndex) {
    this.isTransitioning = true;

    const currentImage = this.section.querySelector('.banner-with-tabbing-main-image.active');
    const currentTab = this.section.querySelector('.banner-tab.active');
    const currentHeader = this.section.querySelector('.tab-header.active');

    if (currentImage) currentImage.classList.remove('active');
    if (currentTab) currentTab.classList.remove('active');
    if (currentHeader) currentHeader.classList.remove('active');

    const newImage = this.section.querySelector(
      '.banner-with-tabbing-main-image[data-id="' + slideIndex + '"]'
    );
    const newTab = this.section.querySelector('.banner-tab[data-id="' + slideIndex + '"]');
    const newHeader = this.section.querySelector('.tab-header[data-id="' + slideIndex + '"]');

    setTimeout(() => {
      if (newImage) newImage.classList.add('active');
      if (newTab) newTab.classList.add('active');
      if (newHeader) newHeader.classList.add('active');

      setTimeout(() => this.updateArrowVisibility(), 50);
      setTimeout(() => this.updateHighlight(), 50);
      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    }, 50);
  }

  updateHighlight() {
    try {
      if (!this.highlight || !this.tabHeader) return;
      const active = this.section.querySelector('.tab-header.active');
      if (!active) return;
      const left = active.offsetLeft - this.tabHeader.scrollLeft;
      const width = active.offsetWidth;
      this.highlight.style.width = width + 'px';
      this.highlight.style.transform = 'translateX(' + left + 'px)';
    } catch(e) {}
  }

  initTouchEvents() {
    if (!this.imageContainer) return;
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let dy = 0;
    const threshold = 50;

    const onStart = (e) => {
      if (this.isTransitioning) return;
      if (e.target && e.target.closest && e.target.closest('.before-after-slider')) return;
      const t = e.touches && e.touches[0] ? e.touches[0] : null;
      if (!t) return;
      startX = t.clientX;
      startY = t.clientY;
      dx = 0;
      dy = 0;
    };

    const onMove = (e) => {
      const t = e.touches && e.touches[0] ? e.touches[0] : null;
      if (!t) return;
      dx = t.clientX - startX;
      dy = t.clientY - startY;
    };

    const onEnd = () => {
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= threshold) {
        if (dx < 0) {
          this.navigateSlide(1);
        } else {
          this.navigateSlide(-1);
        }
      }
    };

    this.imageContainer.addEventListener('touchstart', onStart, { passive: true });
    this.imageContainer.addEventListener('touchmove', onMove, { passive: true });
    this.imageContainer.addEventListener('touchend', onEnd, { passive: true });
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTabbedMedia);
} else {
  initTabbedMedia();
}

function initTabbedMedia() {
  const sections = document.querySelectorAll('.banner-with-tabbing-content');
  sections.forEach((section) => {
    new TabbedMedia(section);
  });
}

// Shopify Section Events
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.banner-with-tabbing-content');
    if (section) {
      new TabbedMedia(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Cleanup if needed
  });
}

